import { useState, useEffect, ReactNode } from "react";
import { Modal, Input, Button, message } from "antd";
import {
  ShareAltOutlined,
  DollarCircleOutlined,
  BookOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { IBookTypeData } from "../../apiservice/userService.type";
import { useBook } from "../../context/DownloadBookProvider";
import { appZIndex } from "../../utils/appconst";
import PaymentOnboardingBook from "../sharedcomponents/PaymentOnboardingBook";
import { useNavigate } from "react-router-dom";
import { IAuthType } from "../../apiservice/authService.type";
import { useAppSelector } from "../../Redux/reduxCustomHook";
import { RootState } from "../../Redux/store";

interface BookActionsProps {
  book: IBookTypeData;
}

const BookActions = ({ book }: BookActionsProps) => {
  const navigate = useNavigate();

  const authData: IAuthType = useAppSelector(
    (state: RootState) => state?.AuthData
  );

  const [isBookmarked, setIsBookmarked] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const { addBook, downloadBookFiles } = useBook();

  // Check if book is bookmarked on component mount
  useEffect(() => {
    const bookmarks = getBookmarksFromLocalStorage();
    setIsBookmarked(bookmarks.some((bookmark) => bookmark.id === book.id));
  }, [book.id]);

  // Get bookmarks from localStorage
  const getBookmarksFromLocalStorage = (): IBookTypeData[] => {
    const bookmarksJson = localStorage.getItem("bookmarks");
    return bookmarksJson ? JSON.parse(bookmarksJson) : [];
  };

  // Save bookmarks to localStorage
  const saveBookmarksToLocalStorage = (bookmarks: IBookTypeData[]) => {
    localStorage.setItem("bookmarks", JSON.stringify(bookmarks));
  };

  // Toggle bookmark status
  const toggleBookmark = () => {
    const bookmarks = getBookmarksFromLocalStorage();

    if (isBookmarked) {
      // Remove from bookmarks
      const updatedBookmarks = bookmarks.filter(
        (bookmark) => bookmark.id !== book.id
      );
      saveBookmarksToLocalStorage(updatedBookmarks);
      setIsBookmarked(false);
      message.success("Book removed from bookmarks");
    } else {
      // Add to bookmarks
      const updatedBookmarks = [...bookmarks, book];
      saveBookmarksToLocalStorage(updatedBookmarks);
      setIsBookmarked(true);
      message.success("Book added to bookmarks");
    }
  };

  const downloadBook = () => {
    addBook(book);
    downloadBookFiles(book);
  };

  // Handle share button click
  const handleShare = () => {
    // Generate share URL
    const shareUrl = `${window.location.origin}/users/book-details/${
      book.id
    }/${encodeURIComponent(book.title.replace(/\s+/g, "_"))}`;
    setShareUrl(shareUrl);
    setShareModalVisible(true);
  };

  // Handle copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        message.success("Link copied to clipboard");
      })
      .catch(() => {
        message.error("Failed to copy link");
      });
  };

  // Handle payment button click
  const handlePayment = () => {
    // Temporarly Access.
    if (book?.isFree) {
      alert("This Book is a free Book, Payment is not required");
      return;
    }

    if (authData?.data?.credentials?.email) {
      setPaymentModalVisible(true);
      return;
    }
    showSignInAlert(
      "Login To Your Account",
      "Please Login to your account to have access to this page.",
      "/auth"
    );
  };

  // Handle download button click
  const handleDownload = () => {
    if (book.bookFileUrl) {
      window.open(`${process.env.PUBLIC_URL + book.bookFileUrl}`, "_blank");
    } else {
      message.info("This book is not available for download");
    }
  };

  // Handle payment form submission
  const handlePaymentSubmit = (values: any) => {
    console.log("Payment values:", values);
    message.success("Payment processed successfully!");
    setPaymentModalVisible(false);
  };

  // Show Sign In Alert
  const showSignInAlert = (title: string, content: ReactNode, url: string) => {
    Modal.confirm({
      title,
      content,
      async onOk() {
        navigate(url);
      },
      onCancel() {
        console.log("Cancelled");
      },
      zIndex: appZIndex.modal,
    });
  };

  return (
    <>
      <div className="w-full bg-gray-100 dark:bg-stone-800 p-3 rounded-lg shadow flex justify-around">
        <ShareAltOutlined
          className="text-xl text-gray-600 dark:text-stone-400 cursor-pointer hover:text-blue-500 dark:hover:text-blue-400"
          onClick={handleShare}
        />
        {/* <DownloadOutlined
          className="text-xl text-gray-600 dark:text-stone-400 cursor-pointer hover:text-blue-500 dark:hover:text-blue-400"
          onClick={downloadBook}
        /> */}

        {!book?.isFree && (
          <DollarCircleOutlined
            className="text-xl text-gray-600 dark:text-stone-400 cursor-pointer hover:text-blue-500 dark:hover:text-blue-400"
            onClick={handlePayment}
          />
        )}

        <BookOutlined
          className={`text-xl cursor-pointer ${
            isBookmarked
              ? "text-yellow-500 dark:text-yellow-400" // Slightly brighter yellow for dark bg
              : "text-gray-600 dark:text-stone-400 hover:text-blue-500 dark:hover:text-blue-400"
          }`}
          onClick={toggleBookmark}
        />
      </div>

      {/* Share Modal */}
      <Modal
        title={
          <span className="text-gray-900 dark:text-stone-100">{`Share this book`}</span>
        }
        open={shareModalVisible}
        zIndex={appZIndex?.modal}
        onCancel={() => setShareModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShareModalVisible(false)}>
            Close
          </Button>,
        ]}
        className="[&_.ant-modal-content]:bg-white [&_.ant-modal-content]:dark:bg-stone-900 [&_.ant-modal-header]:bg-white [&_.ant-modal-header]:dark:bg-stone-900 [&_.ant-modal-header]:border-b [&_.ant-modal-header]:border-gray-200 [&_.ant-modal-header]:dark:border-stone-700 [&_.ant-modal-body]:bg-white [&_.ant-modal-body]:dark:bg-stone-900"
      >
        <div className="mb-4">
          <p className="text-gray-800 dark:text-stone-300">
            Share this book with your friends:
          </p>
          <div className="flex mt-2 gap-2">
            <Input
              value={shareUrl}
              readOnly
              className="flex-1 dark:bg-stone-800 dark:border-stone-600 dark:text-stone-200 [&.ant-input]:dark:bg-stone-800 [&.ant-input]:dark:border-stone-600 [&.ant-input]:dark:text-stone-200"
            />
            <Button
              icon={<CopyOutlined />}
              onClick={copyToClipboard}
              className="dark:bg-stone-700 dark:border-stone-600 dark:text-stone-200 dark:hover:bg-stone-600 [&.ant-btn]:dark:bg-stone-700 [&.ant-btn]:dark:border-stone-600 [&.ant-btn]:dark:text-stone-200 [&.ant-btn]:dark:hover:bg-stone-600"
            >
              Copy
            </Button>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-gray-800 dark:text-stone-300">
            Or share on social media:
          </p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button
              type="primary"
              href={`https://twitter.com/intent/tweet?text=Check out this book: ${
                book.title
              }&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
            >
              Twitter
            </Button>
            <Button
              type="primary"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                shareUrl
              )}`}
              target="_blank"
            >
              Facebook
            </Button>
            <Button
              type="primary"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                shareUrl
              )}`}
              target="_blank"
            >
              LinkedIn
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payment Modal */}
      <Modal
        title={
          <span className="text-gray-900 dark:text-stone-100">{`Purchase ${book.title}`}</span>
        }
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        zIndex={appZIndex.modal}
        width={600}
        className="[&_.ant-modal-content]:bg-white [&_.ant-modal-content]:dark:bg-stone-900 [&_.ant-modal-header]:bg-white [&_.ant-modal-header]:dark:bg-stone-900 [&_.ant-modal-header]:border-b [&_.ant-modal-header]:border-gray-200 [&_.ant-modal-header]:dark:border-stone-700 [&_.ant-modal-body]:bg-white [&_.ant-modal-body]:dark:bg-stone-900 [&_.ant-modal-body]:max-h-[70vh] [&_.ant-modal-body]:overflow-y-auto"
      >
        <div className="bg-transparent">
          <PaymentOnboardingBook bookData={book} />
        </div>
      </Modal>
    </>
  );
};

export default BookActions;
